<!--
  Chat V2 Action Card (desktop redesign)
  - Left: selection checkbox (replaces the mobile Cupertino switch; same toggleActive() logic).
  - Center: small uppercase "ACTION" / "ASYNC" eyebrow, title, description below.
  - Right: ghost "Details" toggle with rotating chevron.
  - Footer: compact neutral buttons, or the in-place done status.
-->
<div
  class="chat-v2-action-card group/card"
  [class.is-inactive]="!isSwitchOn"
  [class.is-done]="isActionProcessed"
  [attr.data-action-index]="index"
>
  <!-- Header Row: title/description, details toggle, include switch -->
  <div class="chat-v2-action-header flex items-center gap-3 px-3.5 py-3">

    <!-- Title, chips and description -->
    <div class="min-w-0 flex-1">
      <!-- Eyebrow: standard neutral label ("ACTION", or "ASYNC" for workflow runs) -->
      <div
        class="chat-v2-action-eyebrow mb-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#A1A1AA] dark:text-[#71717A]"
      >
        {{ isAsync ? config.texts.asyncBadgeText : config.texts.actionBadgeText }}
      </div>
      <div class="flex items-center gap-2 min-w-0">
        <h4
          class="chat-v2-action-title text-[13px] font-medium leading-[18px] truncate text-[#18181B] dark:text-[#F4F4F5] transition-colors"
          [class.line-through]="!isSwitchOn"
          [class.chat-v2-muted]="!isSwitchOn"
          [title]="displayTitle"
        >
          {{ displayTitle }}
        </h4>


      </div>

      <p
        class="chat-v2-action-description mt-0.5 text-[12px] leading-[17px] text-[#71717A] dark:text-[#A1A1AA] truncate transition-colors"
        [class.line-through]="!isSwitchOn"
        [title]="displayDescription"
      >
        {{ displayDescription }}
      </p>
    </div>

    <!-- Ghost Details toggle with rotating chevron -->
    <button
      type="button"
      class="chat-v2-details-toggle -mr-1 inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-[#71717A] hover:bg-[#F4F4F5] hover:text-[#18181B] dark:text-[#A1A1AA] dark:hover:bg-white/5 dark:hover:text-[#F4F4F5] transition-colors cursor-pointer"
      (click)="toggleDetails($event)"
      (keydown.enter)="toggleDetails($event)"
      [attr.aria-expanded]="isDetailsOpen"
    >
      {{ isDetailsOpen ? config.texts.hideDetails : config.texts.showDetails }}
      <i
        class="fa-solid fa-chevron-down text-[9px] transition-transform duration-200"
        [class.rotate-180]="isDetailsOpen"
        aria-hidden="true"
      ></i>
    </button>

    <!-- Compact include switch: decides whether this action runs with the batch -->
    <button
      type="button"
      role="switch"
      [attr.aria-checked]="isSwitchOn"
      [attr.aria-label]="'Toggle action ' + (index + 1) + ' active state'"
      [disabled]="isToggleDisabled"
      (click)="toggleActive($event)"
      (keydown.enter)="toggleActive($event)"
      class="chat-v2-action-toggle relative inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-full p-[2px] transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
      [ngClass]="isSwitchOn ? 'bg-[#27272A] dark:bg-[#E4E4E7]' : 'bg-[#CBD5E1] hover:bg-[#94A3B8] dark:bg-[#52525B] dark:hover:bg-[#71717A]'"
    >
      <span
        aria-hidden="true"
        class="pointer-events-none block h-[16px] w-[16px] rounded-full bg-white dark:bg-[#18181B] shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-transform duration-200"
        [style.transform]="isSwitchOn ? 'translateX(16px)' : 'translateX(0)'"
      ></span>
    </button>
  </div>

  <!-- Expanding Details Drawer: endpoint method + path and optional JSON viewer -->
  @if (isDetailsOpen) {
    <div class="chat-v2-action-details-drawer px-3.5 pb-3">
      @if (httpInfo; as http) {
        <div
          class="chat-v2-endpoint-info flex items-center gap-2"
          [class.mb-2]="hasDisplayPayload"
        >
          <span
            class="chat-v2-http-method inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-none uppercase"
            [ngClass]="getHttpMethodBadgeClass(http.method)"
          >
            {{ http.method }}
          </span>
          <span
            class="chat-v2-endpoint-path font-mono text-[11.5px] text-[#52525B] dark:text-[#A1A1AA] truncate"
            [title]="http.path"
          >
            {{ http.path }}
          </span>
        </div>
      }

      @if (hasDisplayPayload) {
        <div class="chat-v2-json-wrapper rounded-lg border border-[#E4E4E7] dark:border-[#27272A] bg-[#FAFAFA] dark:bg-[#111113] p-2.5">
          <rapider-json-viewer [data]="displayPayload"></rapider-json-viewer>
        </div>
      }
    </div>
  }

  <!-- Footer Row: In-Place Done/Skipped status or Contextual Buttons (hidden while a batch bar decides) -->
  @if (isActionDone || !isSwitchOn || (isLatest && showButtons)) {
  <div class="chat-v2-action-footer flex items-center justify-end gap-2 px-3.5 py-2 border-t border-[#F4F4F5] dark:border-[#27272A]">
    @if (isActionProcessed) {
      <div class="chat-v2-action-status-badge flex items-center gap-1.5 py-0.5">
        <i class="fas fa-circle-check text-[12px] text-[#16A34A]" aria-hidden="true"></i>
        <span class="text-[12px] font-medium text-[#15803D] dark:text-[#4ADE80]">
          {{ config.texts.processed }}
        </span>
      </div>
    } @else if (isActionDone || !isSwitchOn) {
      <div class="chat-v2-action-status-badge flex items-center gap-1.5 py-0.5">
        <i class="fas fa-circle-minus text-[12px] text-[#A1A1AA]" aria-hidden="true"></i>
        <span class="text-[12px] font-medium text-[#71717A]">
          {{ config.texts.skipped }}
        </span>
      </div>
    } @else {
      <!-- Contextual Action Buttons -->
      <div class="chat-v2-action-buttons flex items-center gap-1.5">
        <!-- Secondary Button: Cancel or Discard (ghost) -->
        <button
          type="button"
          class="chat-v2-btn-secondary h-7 px-3 rounded-md text-[12.5px] font-medium text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#18181B] dark:text-[#A1A1AA] dark:hover:bg-white/5 dark:hover:text-[#F4F4F5] transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
          [disabled]="!isActive"
          (click)="handleExecute(false, undefined, $event)"
          (keydown.enter)="handleExecute(false, undefined, $event)"
        >
          {{ buttonLabels.secondary }}
        </button>

        <!-- Server / MCP Action: Allow Once (outline) + Always Allow (solid) -->
        @if (buttonLabels.isServerAction) {
          <button
            type="button"
            class="chat-v2-btn-outline h-7 px-3 rounded-md border border-[#E4E4E7] bg-white text-[12.5px] font-medium text-[#18181B] shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:bg-[#FAFAFA] hover:border-[#D4D4D8] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#F4F4F5] dark:hover:bg-white/5 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
            [disabled]="!isActive"
            (click)="handleExecute(true, { alwaysAllow: false }, $event)"
            (keydown.enter)="handleExecute(true, { alwaysAllow: false }, $event)"
          >
            {{ buttonLabels.primary }}
          </button>

          @if (buttonLabels.primaryOption2) {
            <button
              type="button"
              class="chat-v2-btn-primary h-7 px-3 rounded-md bg-[#18181B] text-[12.5px] font-medium text-white shadow-[0_1px_2px_rgba(0,0,0,0.08)] hover:bg-[#27272A] dark:bg-[#F4F4F5] dark:text-[#18181B] dark:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
              [disabled]="!isActive"
              (click)="handleExecute(true, { alwaysAllow: true }, $event)"
              (keydown.enter)="handleExecute(true, { alwaysAllow: true }, $event)"
            >
              {{ buttonLabels.primaryOption2 }}
            </button>
          }
        } @else {
          <!-- Standard Primary: Save / Confirm (solid neutral) or Delete (destructive) -->
          <button
            type="button"
            class="chat-v2-btn-primary h-7 px-3 rounded-md text-[12.5px] font-medium shadow-[0_1px_2px_rgba(0,0,0,0.08)] transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
            [ngClass]="buttonLabels.isDelete
              ? 'bg-[#DC2626] text-white hover:bg-[#B91C1C]'
              : 'bg-[#18181B] text-white hover:bg-[#27272A] dark:bg-[#F4F4F5] dark:text-[#18181B] dark:hover:bg-white'"
            [disabled]="!isActive"
            (click)="handleExecute(true, undefined, $event)"
            (keydown.enter)="handleExecute(true, undefined, $event)"
          >
            {{ buttonLabels.primary }}
          </button>
        }
      </div>
    }
  </div>
  }
</div>
